/**
 * VerdictFooter Component
 *
 * Footer for Vettis branded pages.
 * 3-column grid: Brand+tagline | Product links | Legal links
 */

import { cn } from '@/lib/utils';
import { ExternalLink, Mail } from 'lucide-react';

interface VerdictFooterProps {
  className?: string;
}

export function VerdictFooter({ className }: VerdictFooterProps) {
  const currentYear = new Date().getFullYear();

  return (
    <footer className={cn('bg-bar-bg/50', className)}>
      <div className="verdict-divider" />
      <div className="w-full px-4 sm:px-6 lg:px-8 xl:px-12 py-12">
        {/* 3-column grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-10">
          {/* Brand + tagline */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <img src="/octoverdict-no-bg.png" alt="Vettis" className="w-6 h-6 object-contain" />
              <span className="text-lg font-semibold">
                <span className="text-verdict-primary">Vettis</span>
              </span>
            </div>
            <p className="text-text-muted text-sm leading-relaxed">
              The high-stake decision engine.
            </p>
            <a
              href="https://x.com/VettisHQ"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-3 inline-flex items-center gap-1.5 text-sm text-text-muted hover:text-text-primary transition-colors w-fit"
            >
              <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
              </svg>
              @VettisHQ
            </a>
          </div>

          {/* Product links */}
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-wider text-text-secondary mb-3">Product</h4>
            <div className="flex flex-col gap-2 text-sm text-text-muted">
              <a href="/dashboard" className="hover:text-text-primary transition-colors w-fit">Dashboard</a>
              <a href="/plans" className="hover:text-text-primary transition-colors w-fit">Pricing</a>
              <a href="/benchmark" className="hover:text-text-primary transition-colors w-fit">Benchmarks</a>
              <a
                href="https://medium.com/@opophehu"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-text-primary transition-colors inline-flex items-center gap-1 w-fit"
              >
                Blog
                <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          </div>

          {/* Legal links */}
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-wider text-text-secondary mb-3">Legal</h4>
            <div className="flex flex-col gap-2 text-sm text-text-muted">
              <a href="/privacy" className="hover:text-text-primary transition-colors w-fit">Privacy Policy</a>
              <a href="/terms" className="hover:text-text-primary transition-colors w-fit">Terms of Service</a>
              <a
                href="mailto:contact@gcholdingsllc.net"
                className="hover:text-text-primary transition-colors inline-flex items-center gap-1.5 w-fit"
              >
                <Mail className="w-3.5 h-3.5" />
                contact@vettis.ai
              </a>
            </div>
          </div>
        </div>

        {/* Bottom Bar - Copyright */}
        <div className="verdict-divider mb-6" />
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-text-muted text-xs">
            &copy; {currentYear} GC Holdings LLC. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
}
